import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt')?.toString()?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    formData.delete('website_alt');

    const submitAddr = form.getAttribute('action') || '';
    if (!submitAddr) {
      setFormError('表单配置错误，请稍后重试');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-background-50 pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于韩漫网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              韩漫网成立于2024年，致力于为全球中文读者提供最优质的韩国漫画阅读体验。我们与韩国多家知名漫画平台合作，收录数千部正版韩漫作品，让每一位读者都能免费畅享下拉式阅读的乐趣。
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-eye-line', title: '我们的愿景', desc: '成为全球最大的中文韩漫阅读平台，让每一位漫画爱好者都能轻松找到并阅读自己喜欢的韩漫作品。' },
              { icon: 'ri-heart-line', title: '我们的使命', desc: '打破语言和地域的障碍，将优质的韩国漫画内容带给每一位中文读者，推动韩漫文化在中国的传播与发展。' },
              { icon: 'ri-star-line', title: '核心价值', desc: '坚持免费阅读、正版授权、优质体验三大原则，为读者提供最好的漫画阅读服务。' },
            ].map(item => (
              <div key={item.title} className="bg-white rounded-lg p-6 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-primary-50 rounded-full flex items-center justify-center">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-background-50 py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
            发展历程
          </h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-accent-200 -translate-x-1/2" />
            {[
              { year: '2024.01', title: '韩漫网正式上线', desc: '完成网站基础架构搭建，首批上线500部韩漫作品' },
              { year: '2024.06', title: '用户突破50万', desc: '凭借优质内容和良好口碑，注册用户突破50万大关' },
              { year: '2024.12', title: '漫画数突破2000部', desc: '与多家韩国漫画平台达成合作，作品库迅速扩展' },
              { year: '2025.06', title: '下拉式阅读上线', desc: '推出全新下拉式阅读模式，大幅提升移动端阅读体验' },
              { year: '2026.01', title: '用户突破200万', desc: '成为中文韩漫阅读领域领先平台，获广泛好评' },
            ].map((item, idx) => (
              <div key={item.year} className={`relative flex items-start mb-8 md:mb-10 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className="hidden md:block flex-1" />
                <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-accent-100" />
                <div className="ml-10 md:ml-0 md:w-1/2 md:px-8">
                  <div className="bg-white rounded-lg p-4 md:p-5 border border-background-200/70">
                    <span className="text-xs text-accent-600 font-medium">{item.year}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: '漫迷小王', rating: 5, text: '韩漫网真的是我见过最好的韩漫平台！更新的速度和韩国同步，画质高清，最重要的是完全免费。下拉式阅读太方便了，推荐给所有喜欢韩漫的朋友！' },
              { name: '追漫达人', rating: 5, text: '以前看了好多韩漫APP都要付费，自从发现韩漫网后就再也没换过。漫画资源丰富，分类清晰，找漫画特别方便。希望能一直做下去！' },
              { name: '深夜读者', rating: 5, text: '作为一个每天都要看漫画的重度用户，韩漫网的下拉式阅读体验真的绝了。晚上躺床上看漫画超舒服，加载快，翻页流畅，五星好评！' },
            ].map(review => (
              <div key={review.name} className="bg-white rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }, (_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{review.text}&rdquo;
                </p>
                <p className="text-sm font-medium text-foreground-900">{review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-background-50 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如果您有任何问题或建议，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-white rounded-lg border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-2 bg-primary-50 rounded-full flex items-center justify-center">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                  </span>
                </div>
                <p className="text-xs text-foreground-500">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-800">contact@hanmanwang.com</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-2 bg-primary-50 rounded-full flex items-center justify-center">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-wechat-line text-lg text-primary-500"></i>
                  </span>
                </div>
                <p className="text-xs text-foreground-500">微信公众号</p>
                <p className="text-sm font-medium text-foreground-800">韩漫网官方</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-2 bg-primary-50 rounded-full flex items-center justify-center">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-weibo-line text-lg text-primary-500"></i>
                  </span>
                </div>
                <p className="text-xs text-foreground-500">官方微博</p>
                <p className="text-sm font-medium text-foreground-800">@韩漫网</p>
              </div>
            </div>

            {/* Contact Form */}
            {formSubmitted ? (
              <div className="bg-white rounded-lg p-8 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 bg-accent-50 rounded-full flex items-center justify-center">
                  <span className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-accent-500"></i>
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复您。</p>
              </div>
            ) : (
              <form
                action="https://readdy.ai/api/form/d9pct8qftmtnfcfh0ko0"
                method="POST"
                onSubmit={handleSubmit}
                data-readdy-form="true"
                className="bg-white rounded-lg p-6 border border-background-200/70 space-y-4"
                id="about-contact-form"
              >
                {formError && (
                  <div className="text-sm text-primary-600 bg-primary-50 px-4 py-2.5 rounded-md">
                    {formError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                </div>
                <div className="honeypot-wrap">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="w-full bg-primary-500 text-white py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
            常见问题
          </h2>
          <div className="space-y-3">
            {[
              { q: '韩漫网的内容是否正版授权？', a: '韩漫网与韩国多家知名漫画平台保持合作关系，所有漫画作品均获得合法授权，确保读者能够安心阅读正版内容。' },
              { q: '如何联系韩漫网？', a: '您可以通过本页面的联系表单、发送邮件至 contact@hanmanwang.com，或关注我们的微信公众号"韩漫网官方"与我们取得联系。' },
              { q: '韩漫网是否有手机APP？', a: '目前韩漫网以网页版为主，但我们采用了响应式设计，在手机浏览器中也能获得出色的阅读体验。APP版本正在开发中，敬请期待。' },
              { q: '漫画加载慢怎么办？', a: '请检查您的网络连接。韩漫网使用CDN加速分发，在正常的网络环境下加载速度很快。如果持续遇到问题，可以尝试清除浏览器缓存。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-white rounded-lg border border-background-200/70 overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 select-none">
                  {faq.q}
                  <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}